import {
  BrandLogo,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { Mail, MapPin, Menu, Phone } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NotificationBell } from './NotificationBell';
import { SocialLinks } from './SocialLinks';
import { useAuth } from '../lib/auth';
import { BRAND } from '../lib/config';
import { CONTACT, telHref } from '../lib/site';

const navLinkClass =
  // No opacity modifiers: the semantic colors are plain hex vars (tailwind-preset.ts),
  // so states are token steps — foreground → brand on hover and on the active route.
  'rounded-md px-2 py-1 text-foreground hover:text-tx-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&.active]:font-medium [&.active]:text-tx-brand';

/**
 * Site header, mirroring the main brand site (biaoperasyonel.com): a dark utility
 * bar with contact details + social links above the navy nav bar carrying the
 * lockup, the app nav, and the auth/language actions.
 *
 * The header is a **dark scope** — `data-theme="dark"` re-binds the semantic tokens
 * for its subtree (docs/15 §5), so `bg-surface`/`text-foreground` and the inline
 * primitives resolve their dark values regardless of the route's own theme. It does
 * not touch `document.documentElement`, so it composes with `ThemeScope` on the dark
 * routes. **Anything portalled out of the subtree does not inherit it** — Radix
 * renders `DropdownMenuContent` into `document.body`, so the phone menu below carries
 * its own `data-theme="dark"`; the same would be needed for a `Dialog` or `toast()`
 * raised from here (none today).
 */
export function SiteHeader() {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = () => logout();

  /**
   * Where the horizontal nav takes over from the phone menu. The **anonymous** header is one
   * link plus two buttons and fits the `sm` column; the **signed-in** one carries four links
   * (in Turkish "Açık Artırmalar" alone runs ~15 characters) plus the bell, logout and the
   * tr/en pills, which together overrun 640px once the panel link joins them. So the
   * authenticated layout stays in the menu until `md` rather than overflowing — and the
   * public showcase header, which does fit, keeps its breakpoint unchanged.
   *
   * Written as whole literal class strings: Tailwind scans source text, so a composed
   * `` `${bp}:flex` `` would never be generated.
   */
  const desktopOnly = state.authenticated ? 'hidden md:flex' : 'hidden sm:flex';
  const phoneOnly = state.authenticated ? 'md:hidden' : 'sm:hidden';

  return (
    <header data-theme="dark" className="bg-surface text-foreground">
      {/* Utility bar (≥lg): contact details + social. Dropped on smaller screens —
          the same details live in the footer, which every page reaches. Each row is
          omitted when its value is configured empty (lib/site.ts). */}
      <div className="hidden border-b border-border bg-sunken lg:block">
        <div className="mx-auto flex max-w-5xl items-center gap-6 px-4 py-2 text-sm">
          <nav aria-label={t('bidder.header.contactBar')} className="flex items-center gap-6">
            {CONTACT.phone && (
              <a href={telHref} className="flex items-center gap-2 hover:underline">
                <Phone aria-hidden className="h-4 w-4 text-tx-brand" />
                {CONTACT.phone}
              </a>
            )}
            {CONTACT.email && (
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-2 hover:underline"
              >
                <Mail aria-hidden className="h-4 w-4 text-tx-brand" />
                {CONTACT.email}
              </a>
            )}
            {CONTACT.address && (
              <span className="flex items-center gap-2 text-muted-foreground">
                <MapPin aria-hidden className="h-4 w-4 text-tx-brand" />
                {CONTACT.address}
              </span>
            )}
          </nav>
          <div className="ml-auto">
            <SocialLinks size="sm" />
          </div>
        </div>
      </div>

      {/* Nav bar. */}
      <div className="mx-auto flex max-w-5xl items-center gap-x-4 px-4 py-3">
        <Link
          to="/"
          aria-label={BRAND}
          className="flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {/* Always the white lockup — the header is a dark scope on every route (docs/15 §3). */}
          <BrandLogo variant="white" alt={BRAND} />
        </Link>

        {/* Desktop nav — centred between the lockup and the actions, as on the brand site. */}
        <nav className={`${desktopOnly} items-center gap-6 text-sm md:mx-auto`}>
          <Link to="/" className={navLinkClass}>
            {t('bidder.nav.auctions')}
          </Link>
          {state.authenticated && (
            <>
              <Link to="/me" className={navLinkClass}>
                {t('bidder.nav.dashboard')}
              </Link>
              <Link to="/me/bids" className={navLinkClass}>
                {t('bidder.nav.myBids')}
              </Link>
              <Link to="/membership" className={navLinkClass}>
                {t('bidder.nav.membership')}
              </Link>
            </>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {state.authenticated && <NotificationBell />}
          {/* Desktop auth actions: Kayıt Ol is the shell's one filled button. */}
          <div className={`${desktopOnly} items-center gap-2`}>
            {state.authenticated ? (
              <Button variant="outline" size="sm" onClick={onLogout}>
                {t('bidder.nav.logout')}
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate({ to: '/login' })}>
                  {t('bidder.nav.login')}
                </Button>
                {/* The gradient pill is the group site's front-door CTA (its "Giriş Yap");
                    ours is Kayıt Ol, the one action the whole public page exists to invite. */}
                <Button variant="brand" size="sm" onClick={() => navigate({ to: '/register' })}>
                  {t('bidder.nav.register')}
                </Button>
              </>
            )}
          </div>
          <LanguageSwitcher />
          {/* Phone menu: nav + auth actions in one dropdown, up to the breakpoint above. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={phoneOnly}
                aria-label={t('common.actions')}
              >
                <Menu aria-hidden className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            {/* Portalled to <body>, so it needs its own dark scope to match the header
                it drops out of — on the phone breakpoint this menu IS the nav. */}
            <DropdownMenuContent data-theme="dark" align="end" className={phoneOnly}>
              <DropdownMenuItem asChild>
                <Link to="/">{t('bidder.nav.auctions')}</Link>
              </DropdownMenuItem>
              {state.authenticated && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/me">{t('bidder.nav.dashboard')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/me/bids">{t('bidder.nav.myBids')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/membership">{t('bidder.nav.membership')}</Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              {state.authenticated ? (
                <DropdownMenuItem onSelect={onLogout}>{t('bidder.nav.logout')}</DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onSelect={() => void navigate({ to: '/login' })}>
                    {t('bidder.nav.login')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void navigate({ to: '/register' })}>
                    {t('bidder.nav.register')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
